// TODO: split this function into 2 different that return schema amd initialForm, and provide meaningful names
import { getDateWithoutTimezoneShift } from '@lender/utils';
import dayjs from 'dayjs';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import { array, isSchema, object } from 'yup';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { getFieldValidationFunction, transformNameInModelIntoFormName } from 'utils/formGenerator';

import { normalizeViewPhoneNumber } from './data';

// FormGenerator `getOptionLabel` factory: resolve a select option's display label from its VALUE
// against a specific options list. Use when two configs share a form key but expose different label
// sets for the same value (e.g. chattel `homeType`: 'Double-Wide' on the Property tab vs 'Double' on
// the Manufactured Home Details tab) -- without it, whichever config hydrates the shared key last
// leaves its option object on the form and the other field renders the wrong label.
export const getOptionLabelByValue = options => option => {
  const rawValue = option?.value ?? option;

  return options.find(({ value }) => value === rawValue)?.name ?? '';
};

export const getDateValueInForm = (valueInModel, inputProps = {}) => {
  if (!valueInModel) {
    return;
  }

  if (inputProps.type === FieldInputType.YEAR) {
    return dayjs(valueInModel.toString());
  }

  const dateToFormat = inputProps.includeLocalOffset
    ? valueInModel.toString()
    : getDateWithoutTimezoneShift(valueInModel.toString());

  return dayjs(dateToFormat);
};

export const getConfigInfo = ({ applicationData, config, isNestedForm }) => {
  const rawSchema = {};
  const fieldsConfig = {};
  const initialFormValues = {};
  const observableFieldsNames = [];
  const fieldsNames = [];

  const fieldHandler = ({
    type,
    noValidate,
    inputProps,
    nameInModel,
    additionalContentBySelectedOption,
    noObserve,
  }) => {
    const fieldNameInForm = isNestedForm
      ? nameInModel
      : transformNameInModelIntoFormName(nameInModel);
    const valueInModel = _.get(applicationData, nameInModel);

    if (!noObserve) {
      observableFieldsNames.push(fieldNameInForm);
    }

    fieldsNames.push(fieldNameInForm);

    const getValueInForm = () => {
      switch (type) {
        case FieldType.SELECT: {
          // A config assembled outside its owning modal can miss an options injection
          // (FZ-4286 buyDown in LoanEditorCustomFormDrawer) -- degrade to an empty list
          // instead of crashing the whole page through the ErrorBoundary.
          const options = inputProps.staticOptions ?? inputProps.options ?? [];

          if (inputProps.multiple) {
            return options.filter(option => valueInModel?.includes(option.value));
          }

          return options.find(option => valueInModel === option.value);
        }

        case FieldType.DATE:
          return getDateValueInForm(valueInModel, inputProps);

        case FieldType.INPUT:

        case FieldType.TEL:
          if (inputProps.type === FieldInputType.TEL) {
            return normalizeViewPhoneNumber(valueInModel);
          }

        // eslint-disable-next-line no-fallthrough
        default:
          return valueInModel;
      }
    };

    const valueInForm = getValueInForm();

    _.set(initialFormValues, fieldNameInForm, valueInForm);

    const fieldProps = {
      fieldType: type,
      noValidate,
      noObserve,
      ...inputProps,
    };

    _.set(rawSchema, fieldNameInForm, getFieldValidationFunction(fieldProps));
    _.set(fieldsConfig, fieldNameInForm, fieldProps);

    if (additionalContentBySelectedOption) {
      Object.values(additionalContentBySelectedOption).forEach(({ groups }) =>
        groups.forEach(groupsMapper),
      );
    }
  };

  const fieldMapper = field => {
    if (field.type === FieldType.GROUP) {
      return field.fields.forEach(fieldMapper);
    }

    if (field.type === FieldType.COMBINED) {
      return field.combinedFields.forEach(subField =>
        fieldHandler({ ...subField, type: subField.type ?? FieldType.INPUT }),
      );
    }

    fieldHandler(field);
  };

  const groupsMapper = ({ fields }) => {
    fields.forEach(fieldMapper);
  };

  config.groups.forEach(groupsMapper);

  if (isNestedForm) {
    const nestedFormHandler = schema => {
      const pairsArray = _.toPairs(schema);

      return pairsArray.reduce((acc, [key, value]) => {
        const isSchemaExist = isSchema(value);

        if (isSchemaExist) {
          acc[key] = value;

          return acc;
        }

        const isArray = _.isArray(value);
        const wrapperFunction = isArray ? array : object;
        const wrappedValue = isArray ? object(value[0]) : nestedFormHandler(value);

        acc[key] = wrapperFunction(wrappedValue);

        return acc;
      }, {});
    };

    const processedSchema = nestedFormHandler(rawSchema);

    return {
      schema: object().shape(processedSchema),
      fieldsConfig,
      initialFormValues,
      initialConfig: config,
      observableFieldsNames,
      fieldsNames,
    };
  }

  return {
    schema: object().shape(rawSchema),
    fieldsConfig,
    initialFormValues,
    initialConfig: config,
    observableFieldsNames,
    fieldsNames,
  };
};

export const getChangedFieldsByConfig = ({ initialConfig, pendingChangesData }) => {
  const changedFields = {};

  const changedFieldAccumulator = ({
    type,
    inputProps,
    nameInModel,
    additionalContentBySelectedOption,
  }) => {
    const isFieldChangesExist = _.has(pendingChangesData, nameInModel);

    if (isFieldChangesExist) {
      const diffValues = _.get(pendingChangesData, nameInModel);
      const { newValue, initialValue } = diffValues || {};

      const hasFieldShallowChanged = [initialValue, newValue].every(v => _.isNil(v) || v === '');
      const hasFieldStrictChanged = initialValue !== newValue;
      const hasNotRequiredFieldShallowChanged = !inputProps?.required && hasFieldShallowChanged;
      const isFieldChanged = !hasNotRequiredFieldShallowChanged && hasFieldStrictChanged;

      if (type === FieldType.CHECKBOX_GROUP) {
        changedFields[nameInModel] = diffValues;
      }

      if (isFieldChanged) {
        changedFields[nameInModel] = diffValues.newValue;
      }
    }

    if (additionalContentBySelectedOption) {
      Object.values(additionalContentBySelectedOption).forEach(({ groups }) =>
        groups.forEach(groupsMapper),
      );
    }
  };

  const groupsMapper = ({ fields }) => {
    fields.forEach(field => {
      if (field.type === FieldType.GROUP) {
        return field.fields.forEach(changedFieldAccumulator);
      }

      if (field.type === FieldType.COMBINED) {
        return field.combinedFields.forEach(changedFieldAccumulator);
      }

      changedFieldAccumulator(field);
    });
  };

  initialConfig.groups.forEach(groupsMapper);

  return { changedFields };
};

export const fillConfigFieldsWithKeys = config => {
  const fieldMapper = field => {
    const fieldConfig = {
      ...field,
      inputProps: {
        ...field?.inputProps,
        key: field?.inputProps?.key || nanoid(),
      },
    };

    const additionalConfig = {};

    if (field?.additionalContentBySelectedOption) {
      Object.keys(field?.additionalContentBySelectedOption).forEach(key => {
        additionalConfig[key] = fillConfigFieldsWithKeys(
          field?.additionalContentBySelectedOption[key],
        );
      });

      return {
        ...fieldConfig,
        additionalContentBySelectedOption: additionalConfig,
      };
    }

    return fieldConfig;
  };

  const fieldsMapper = field => {
    if (field.type === FieldType.GROUP) {
      return groupsMapper(field);
    }

    return fieldMapper(field);
  };

  const groupsMapper = ({ fields, ...restProps }) => {
    return {
      ...restProps,
      key: restProps?.key || nanoid(),
      fields: fields.map(fieldsMapper),
    };
  };

  return { groups: config.groups?.map(groupsMapper) };
};
